import { MenuCategory } from '../types/api';

/**
 * Дерево разделов каталога.
 *
 * Бэк отдаёт разделы плоским списком в порядке обхода — родитель, следом его
 * поддерево. Здесь этот список превращается в дерево и обратно: кабинету
 * нужны и вложенный вид (панель разделов), и плоский (выпадающие списки).
 *
 * Главное правило каталога: товары лежат только в конечных разделах. Всё, что
 * тут считается — счётчики ветки, список допустимых родителей, — служит ему.
 */
export interface CategoryNode extends MenuCategory {
  children: CategoryNode[];
}

/**
 * Собрать дерево по `parent_id`.
 *
 * Раздел, чей родитель в списке отсутствует (его отфильтровали или он ещё не
 * подъехал), становится корневым: потерять его молча хуже, чем показать не на
 * своём уровне.
 */
export function buildCategoryTree(categories: MenuCategory[]): CategoryNode[] {
  const nodes = new Map<number, CategoryNode>();
  categories.forEach((category) => nodes.set(category.id, { ...category, children: [] }));

  const roots: CategoryNode[] = [];

  nodes.forEach((node) => {
    const parent = node.parent_id == null ? undefined : nodes.get(node.parent_id);
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
}

/** Развернуть дерево обратно в плоский список в порядке обхода. */
export function flattenCategoryTree(nodes: CategoryNode[]): CategoryNode[] {
  return nodes.flatMap((node) => [node, ...flattenCategoryTree(node.children)]);
}

/** Раздел вместе со всем поддеревом. */
export function subtreeIds(node: CategoryNode): number[] {
  return flattenCategoryTree([node]).map((item) => item.id);
}

/** Сколько позиций в ветке, считая подразделы. */
export function branchItemsCount(node: CategoryNode): number {
  return flattenCategoryTree([node]).reduce((sum, item) => sum + (item.menu_items_count ?? 0), 0);
}

/** Конечный раздел — тот, куда можно класть товары. */
export function isLeafCategory(category: MenuCategory, categories: MenuCategory[]): boolean {
  if (category.is_leaf != null) return category.is_leaf;
  if (category.children_count != null) return category.children_count === 0;

  return !categories.some((candidate) => candidate.parent_id === category.id);
}

/** Только конечные разделы: их и предлагают в формах позиции. */
export function leafCategories(categories: MenuCategory[]): MenuCategory[] {
  return categories.filter((category) => isLeafCategory(category, categories));
}

/**
 * Подпись раздела с путём от корня: «Бар / Безалкогольные / Чаи».
 *
 * Без пути выпадающий список из трёх «Чаи» ничего не говорит партнёру.
 */
export function categoryPathLabel(category: MenuCategory, categories: MenuCategory[]): string {
  if (category.path?.length) {
    return category.path.map((node) => node.name).join(' / ');
  }

  const byId = new Map(categories.map((item) => [item.id, item]));
  const names: string[] = [];

  let current: MenuCategory | undefined = category;
  let guard = 0;

  while (current && guard++ < 16) {
    names.unshift(current.name);
    current = current.parent_id == null ? undefined : byId.get(current.parent_id);
  }

  return names.join(' / ');
}

/**
 * Разделы, которые годятся в родители для `moving`.
 *
 * Исключены: сам раздел с поддеревом (иначе цикл), разделы с товарами
 * (товары только в листьях) и последний разрешённый уровень.
 *
 * `moving === undefined` — выбор родителя для нового раздела.
 */
export function allowedParents(
  categories: MenuCategory[],
  moving: MenuCategory | undefined,
  maxDepth: number,
): MenuCategory[] {
  const tree = buildCategoryTree(categories);
  const movingNode = moving
    ? flattenCategoryTree(tree).find((node) => node.id === moving.id)
    : undefined;

  const forbidden = new Set(movingNode ? subtreeIds(movingNode) : []);
  const height = movingNode ? subtreeHeight(movingNode) : 1;

  return categories.filter((category) => {
    if (forbidden.has(category.id)) return false;
    if ((category.menu_items_count ?? 0) > 0) return false;

    return category.depth + 1 + height <= maxDepth;
  });
}

/** Высота поддерева в уровнях: у листа — 1. */
export function subtreeHeight(node: CategoryNode): number {
  if (node.children.length === 0) return 1;

  return 1 + Math.max(...node.children.map(subtreeHeight));
}
